import React, { useState, useEffect } from 'react';
import Parse from 'parse';
import { BethelAppClasses, bethelDate, bethelQuery, bethelRun } from '../../api/config';
import { Banner, Button, Card, CardHeader, EmptyState, cx } from '../ui';

// canManageTypes: whether to show the add/activate/deactivate controls for
// giving types. Those writes are a pastoral action on the app server, so
// outside Pastor's Tools (e.g. the Finance view) the list is read-only.
const GivingDashboard = ({ canManageTypes = true }) => {
    const [payments, setPayments] = useState([]);
    const [givingTypes, setGivingTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [showAddTypeForm, setShowAddTypeForm] = useState(false);
    const [dateRange, setDateRange] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0],
    });
    const [summary, setSummary] = useState({ total: 0, byType: {}, count: 0 });

    useEffect(() => {
        fetchGivingData();
    }, [dateRange]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (successMessage) {
            const timer = setTimeout(() => setSuccessMessage(''), 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    const fetchGivingData = async () => {
        try {
            setLoading(true);
            setError('');

            // Direct fetch to the app server — no global SDK switch. See
            // bethelRun/bethelQuery in api/config.js for why.
            const typesData = await bethelRun('getActiveGivingTypes');
            setGivingTypes(typesData || []);

            const paymentsData = await bethelQuery(BethelAppClasses.Payment, {
                where: {
                    status: 'completed',
                    createdAt: {
                        $gte: bethelDate(dateRange.startDate),
                        $lte: bethelDate(dateRange.endDate + 'T23:59:59'),
                    },
                },
                include: 'givingType,user',
                order: '-createdAt',
                limit: 1000,
            });
            setPayments(paymentsData);

            calculateSummary(paymentsData, typesData);
        } catch (err) {
            console.error('Error fetching giving data:', err);
            setError('We could not load the giving data. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const calculateSummary = (paymentsData, typesData) => {
        let total = 0;
        const byType = {};

        typesData.forEach((type) => {
            byType[type.get('givingTypeName')] = { amount: 0, count: 0, typeId: type.id };
        });

        paymentsData.forEach((payment) => {
            const amount = amountDollars(payment);
            total += amount;
            const typeName = payment.get('givingType');
            if (byType[typeName]) {
                byType[typeName].amount += amount;
                byType[typeName].count += 1;
            }
        });

        setSummary({ total, byType, count: paymentsData.length });
    };

    // A payment's amount in dollars. The app stores giving inconsistently:
    // guest checkouts save the raw Stripe amount (cents), while logged-in gifts
    // save dollars. Without this, guest giving shows up 100x too high and the
    // totals are wildly inflated. Guests are identified by isGuest, or by the
    // "guest_payment…" idempotency key when that flag wasn't set.
    const amountDollars = (payment) => {
        const raw = payment.get('amount') || 0;
        const key = payment.get('idempotencyKey') || '';
        const isGuest = payment.get('isGuest') === true || key.startsWith('guest_payment');
        return isGuest ? raw / 100 : raw;
    };

    const formatCurrency = (amount) =>
        new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

    const formatDate = (date) =>
        new Date(date).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });

    const createGivingType = async (e) => {
        e.preventDefault();
        setError('');
        setSuccessMessage('');

        const typeName = e.target.typeName.value.trim();
        const description = e.target.description.value.trim();
        const form = e.target;

        if (!typeName) {
            setError('A giving-type name is required.');
            return;
        }

        try {
            // The forms server verifies we're a pastor and writes it to the app.
            await Parse.Cloud.run('pastorsCreateGivingType', {
                name: typeName,
                description,
                order: givingTypes.length, // add it at the end
            });

            setSuccessMessage(`Giving type "${typeName}" created.`);
            setShowAddTypeForm(false);
            form.reset();
            fetchGivingData();
        } catch (err) {
            console.error('Error creating giving type:', err);
            setError(err.message || 'We could not create the giving type. Please try again.');
        }
    };

    const toggleGivingTypeStatus = async (givingTypeId, currentStatus) => {
        try {
            await Parse.Cloud.run('pastorsSetGivingTypeActive', {
                givingTypeId,
                active: !currentStatus,
            });

            setSuccessMessage(`Giving type ${!currentStatus ? 'activated' : 'deactivated'}.`);
            fetchGivingData();
        } catch (err) {
            console.error('Error updating giving type:', err);
            setError(err.message || 'We could not update the giving type. Please try again.');
        }
    };

    const exportToCSV = () => {
        const headers = ['Date', 'Type', 'Amount', 'Status'];
        const rows = payments.map((payment) => [
            formatDate(payment.get('createdAt')),
            payment.get('givingType') || 'Unknown',
            formatCurrency(amountDollars(payment)),
            payment.get('status'),
        ]);

        const csvContent = [headers.join(','), ...rows.map((row) => row.map((cell) => `"${cell}"`).join(','))].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `giving-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const summaryCards = [
        { label: 'Total giving', value: formatCurrency(summary.total) },
        { label: 'Transactions', value: summary.count },
        { label: 'Average gift', value: summary.count > 0 ? formatCurrency(summary.total / summary.count) : '$0.00' },
    ];

    return (
        <div className="space-y-4">
            {error && <Banner tone="danger">{error}</Banner>}
            {successMessage && <Banner tone="success">{successMessage}</Banner>}

            {/* Date range */}
            <Card>
                <div className="flex flex-wrap items-end gap-4 px-5 py-4">
                    <div>
                        <label htmlFor="giving-start" className="block text-sm font-medium text-neutral-800">
                            Start date
                        </label>
                        <input
                            id="giving-start"
                            type="date"
                            className="mt-1"
                            value={dateRange.startDate}
                            onChange={(e) => setDateRange((prev) => ({ ...prev, startDate: e.target.value }))}
                        />
                    </div>
                    <div>
                        <label htmlFor="giving-end" className="block text-sm font-medium text-neutral-800">
                            End date
                        </label>
                        <input
                            id="giving-end"
                            type="date"
                            className="mt-1"
                            value={dateRange.endDate}
                            onChange={(e) => setDateRange((prev) => ({ ...prev, endDate: e.target.value }))}
                        />
                    </div>
                    <Button variant="secondary" onClick={fetchGivingData}>
                        Update
                    </Button>
                    <Button variant="ghost" onClick={exportToCSV} disabled={payments.length === 0}>
                        Export CSV
                    </Button>
                </div>
            </Card>

            {/* Summary */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {summaryCards.map((c) => (
                    <Card key={c.label} className="px-5 py-4">
                        <p className="text-xs font-medium uppercase tracking-wider text-neutral-500">{c.label}</p>
                        <p className="mt-1 font-display text-2xl font-semibold tabular-nums text-neutral-900">{c.value}</p>
                    </Card>
                ))}
            </div>

            {/* Giving types */}
            <Card>
                <CardHeader
                    title="Giving types"
                    description="What people can give toward in the member app."
                    actions={
                        canManageTypes && (
                            <Button size="sm" variant={showAddTypeForm ? 'ghost' : 'secondary'} onClick={() => setShowAddTypeForm(!showAddTypeForm)}>
                                {showAddTypeForm ? 'Cancel' : 'Add type'}
                            </Button>
                        )
                    }
                />

                {canManageTypes && showAddTypeForm && (
                    <form onSubmit={createGivingType} className="grid gap-4 border-b border-neutral-100 px-5 py-4 sm:grid-cols-2">
                        <div>
                            <label htmlFor="type-name" className="block text-sm font-medium text-neutral-800">
                                Name
                            </label>
                            <input id="type-name" type="text" name="typeName" required className="mt-1" placeholder="Missions, Building Fund…" />
                        </div>
                        <div>
                            <label htmlFor="type-desc" className="block text-sm font-medium text-neutral-800">
                                Description (optional)
                            </label>
                            <input id="type-desc" type="text" name="description" className="mt-1" placeholder="Short description" />
                        </div>
                        <div className="sm:col-span-2">
                            <Button type="submit" variant="primary">
                                Create type
                            </Button>
                        </div>
                    </form>
                )}

                {givingTypes.length === 0 ? (
                    <EmptyState title="No giving types yet" description="Add one so people have something to give toward." />
                ) : (
                    <ul className="divide-y divide-neutral-100">
                        {givingTypes.map((type) => {
                            const active = type.get('active');
                            return (
                                <li key={type.id} className="flex items-center justify-between gap-4 px-5 py-3.5">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <p className="truncate text-sm font-medium text-neutral-900">
                                                {type.get('givingTypeName')}
                                            </p>
                                            <span
                                                className={cx(
                                                    'inline-flex items-center rounded-full px-2 py-0.5 text-xxs font-medium ring-1 ring-inset',
                                                    active
                                                        ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
                                                        : 'bg-neutral-100 text-neutral-500 ring-neutral-200'
                                                )}
                                            >
                                                {active ? 'Active' : 'Inactive'}
                                            </span>
                                        </div>
                                        {type.get('givingTypeDescription') && (
                                            <p className="mt-0.5 truncate text-xs text-neutral-500">
                                                {type.get('givingTypeDescription')}
                                            </p>
                                        )}
                                    </div>
                                    {canManageTypes && (
                                        <Button size="sm" variant="ghost" onClick={() => toggleGivingTypeStatus(type.id, active)}>
                                            {active ? 'Deactivate' : 'Activate'}
                                        </Button>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Card>

            {/* By type */}
            <Card>
                <CardHeader title="Giving by type" description={`${dateRange.startDate} to ${dateRange.endDate}`} />
                {loading ? (
                    <EmptyState title="Loading…" />
                ) : Object.keys(summary.byType).length === 0 ? (
                    <EmptyState title="Nothing to show" description="No giving in this date range." />
                ) : (
                    <div className="space-y-4 px-5 py-4">
                        {Object.entries(summary.byType).map(([typeName, data]) => {
                            const pct = summary.total > 0 ? (data.amount / summary.total) * 100 : 0;
                            return (
                                <div key={data.typeId}>
                                    <div className="mb-1 flex items-center justify-between text-sm">
                                        <span className="font-medium text-neutral-800">{typeName}</span>
                                        <span className="tabular-nums text-neutral-500">
                                            {formatCurrency(data.amount)} · {Math.round(pct)}%
                                        </span>
                                    </div>
                                    <div className="h-2 w-full rounded-full bg-neutral-100">
                                        <div className="h-2 rounded-full bg-brand-600" style={{ width: `${pct}%` }} />
                                    </div>
                                    <p className="mt-1 text-xs text-neutral-400">{data.count} transactions</p>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            {/* Recent transactions */}
            <Card className="overflow-hidden">
                <CardHeader title="Recent transactions" />
                {payments.length === 0 ? (
                    <EmptyState title="No transactions" description="Nothing recorded for this date range." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-neutral-200">
                            <thead className="bg-neutral-50">
                                <tr className="text-left text-xxs uppercase tracking-wider text-neutral-500">
                                    <th scope="col" className="px-5 py-2.5 font-semibold">Date</th>
                                    <th scope="col" className="px-5 py-2.5 font-semibold">Type</th>
                                    <th scope="col" className="px-5 py-2.5 text-right font-semibold">Amount</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {payments.slice(0, 10).map((payment) => (
                                    <tr key={payment.id} className="hover:bg-neutral-50/70">
                                        <td className="whitespace-nowrap px-5 py-3 text-sm text-neutral-600">
                                            {formatDate(payment.get('createdAt'))}
                                        </td>
                                        <td className="px-5 py-3 text-sm text-neutral-800">
                                            {payment.get('givingType') || 'Unknown'}
                                        </td>
                                        <td className="whitespace-nowrap px-5 py-3 text-right text-sm font-medium tabular-nums text-neutral-900">
                                            {formatCurrency(amountDollars(payment))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
};

export default GivingDashboard;
